"use client";
import { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import { Box, CircularProgress } from "@mui/material";
import { Header } from "@components/header";
import { chooseCalendarBuilder } from "@components/calendar-v2/choose-builder";
import { hasV1Draft } from "@components/calendar-v2/io/draft-store";

const Loading = () => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "100vh",
    }}
  >
    <CircularProgress />
  </Box>
);

const CalendarEditor = dynamic(
  () => import("@components/calendar/CalendarEditor"),
  { ssr: false, loading: Loading }
);

const CalendarBuilderV2 = dynamic(
  () => import("@components/calendar-v2/builder/CalendarBuilderV2"),
  { ssr: false, loading: Loading }
);

/**
 * v2 everywhere (see chooseCalendarBuilder). A customer with an unfinished v1
 * draft stays on v1 unless v2 is asked for explicitly.
 */
function useCalendarBuilderChoice(): "v1" | "v2" | null {
  const [choice, setChoice] = useState<"v1" | "v2" | null>(null);
  useEffect(() => {
    let alive = true;
    const search = new URLSearchParams(window.location.search);
    const env = process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT;
    const decide = (draft: boolean) => {
      if (alive) setChoice(chooseCalendarBuilder({ env, search, hasV1Draft: draft }));
    };
    // The v1 draft (IndexedDB) only matters when it could change the answer.
    const withoutDraft = chooseCalendarBuilder({ env, search, hasV1Draft: false });
    const withDraft = chooseCalendarBuilder({ env, search, hasV1Draft: true });
    if (withoutDraft === withDraft) decide(false);
    else hasV1Draft().then(decide, () => decide(false));
    return () => {
      alive = false;
    };
  }, []);
  return choice;
}

export default function CalendarPage() {
  const builder = useCalendarBuilderChoice();
  return (
    <>
      <Header sticky={false} />
      {builder === null ? <Loading /> : builder === "v2" ? <CalendarBuilderV2 /> : <CalendarEditor />}
    </>
  );
}
